import React, { useEffect, useMemo, useRef, useState } from "react";
import * as pdfjsLib from "pdfjs-dist/build/pdf.mjs";
import { PDFDocument } from "pdf-lib";
import Dropzone from "./Dropzone";

pdfjsLib.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL}/pdf.worker.min.mjs`;

function baseName(filename = "document.pdf") {
  return filename.replace(/\.[^/.]+$/, "");
}

// Parse "1,3-5,9" -> Set([1,3,4,5,9])
function parseRanges(text, max) {
  const set = new Set();
  const raw = (text || "").replace(/\s+/g, "");
  if (!raw) return set;

  raw.split(",").forEach((part) => {
    if (!part) return;
    if (part.includes("-")) {
      const [a, b] = part.split("-").map((x) => parseInt(x, 10));
      if (!Number.isFinite(a) || !Number.isFinite(b)) return;
      const start = Math.min(a, b);
      const end = Math.max(a, b);
      for (let i = start; i <= end; i++) if (i >= 1 && i <= max) set.add(i);
    } else {
      const n = parseInt(part, 10);
      if (Number.isFinite(n) && n >= 1 && n <= max) set.add(n);
    }
  });

  return set;
}

export default function PdfPagesEditor() {
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);

  const [pdfInfo, setPdfInfo] = useState({ count: null, name: "" });

  // pages in current order
  // { id, index0, thumbUrl, removed }
  const [pages, setPages] = useState([]);

  // optional: quick delete input
  const [removeText, setRemoveText] = useState("");

  const dragFrom = useRef(null);
  const thumbUrlsRef = useRef([]);

  const cleanupThumbs = () => {
    thumbUrlsRef.current.forEach((u) => URL.revokeObjectURL(u));
    thumbUrlsRef.current = [];
  };

  const resetAll = () => {
    cleanupThumbs();
    setFile(null);
    setPdfInfo({ count: null, name: "" });
    setPages([]);
    setRemoveText("");
  };

  const onPick = (f) => {
    resetAll();
    if (!f) return;
    setFile(f);
    setPdfInfo({ count: null, name: f.name });
  };

  // Render thumbnails for all pages (small, fast)
  useEffect(() => {
    let cancelled = false;

    async function buildThumbs() {
      if (!file) return;

      setLoading(true);
      cleanupThumbs();

      try {
        const buf = await file.arrayBuffer();
        const pdf = await pdfjsLib.getDocument({ data: buf }).promise;

        if (cancelled) return;

        const count = pdf.numPages;
        setPdfInfo({ count, name: file.name });

        const out = [];

        for (let p = 1; p <= count; p++) {
          const page = await pdf.getPage(p);
          const viewport = page.getViewport({ scale: 0.35 }); // miniature

          const canvas = document.createElement("canvas");
          const ctx = canvas.getContext("2d", { alpha: false });
          canvas.width = Math.floor(viewport.width);
          canvas.height = Math.floor(viewport.height);

          await page.render({ canvasContext: ctx, viewport }).promise;

          const blob = await new Promise((resolve, reject) => {
            canvas.toBlob(
              (b) =>
                b ? resolve(b) : reject(new Error("toBlob thumb échoué")),
              "image/jpeg",
              0.8
            );
          });

          const url = URL.createObjectURL(blob);
          thumbUrlsRef.current.push(url);

          out.push({
            id: `p-${p}-${Math.random().toString(16).slice(2)}`,
            index0: p - 1, // original index in pdf-lib (0-based)
            pageNum: p, // original human number
            thumbUrl: url,
            removed: false,
          });
        }

        if (!cancelled) setPages(out);
      } catch (e) {
        console.error(e);
        alert("Impossible de lire ce PDF.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    buildThumbs();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [file]);

  useEffect(() => {
    return () => cleanupThumbs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const keptCount = useMemo(
    () => pages.filter((p) => !p.removed).length,
    [pages]
  );

  const toggleRemoved = (id) => {
    setPages((prev) =>
      prev.map((p) => (p.id === id ? { ...p, removed: !p.removed } : p))
    );
  };

  const removeOne = (id) => {
    setPages((prev) =>
      prev.map((p) => (p.id === id ? { ...p, removed: true } : p))
    );
  };

  // drag reorder (HTML5)
  const onDragStart = (idx) => {
    dragFrom.current = idx;
  };

  const onDrop = (idx) => {
    const from = dragFrom.current;
    dragFrom.current = null;
    if (from === null || from === undefined) return;
    if (from === idx) return;

    setPages((prev) => {
      const next = [...prev];
      const [moved] = next.splice(from, 1);
      next.splice(idx, 0, moved);
      return next;
    });
  };

  const applyRemoveText = () => {
    if (typeof pdfInfo.count !== "number") return;
    const toRemove = parseRanges(removeText, pdfInfo.count);
    if (!toRemove.size) return;

    setPages((prev) =>
      prev.map((p) => {
        // p.pageNum = original page number
        if (toRemove.has(p.pageNum)) return { ...p, removed: true };
        return p;
      })
    );
  };

  const downloadEditedPdf = async () => {
    if (!file) return;
    if (typeof pdfInfo.count !== "number") return;

    const kept = pages.filter((p) => !p.removed);
    if (!kept.length) {
      alert("Tu ne peux pas supprimer toutes les pages.");
      return;
    }

    setLoading(true);
    try {
      const inputBytes = await file.arrayBuffer();
      const src = await PDFDocument.load(inputBytes);
      const out = await PDFDocument.create();

      // indexes in final order
      const indexes = kept.map((p) => p.index0);
      const copied = await out.copyPages(src, indexes);
      copied.forEach((pg) => out.addPage(pg));

      const bytes = await out.save();
      const blob = new Blob([bytes], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);

      const a = document.createElement("a");
      a.href = url;
      a.download = `${baseName(file.name)}-modifie.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();

      setTimeout(() => URL.revokeObjectURL(url), 1500);
    } catch (e) {
      console.error(e);
      alert("Erreur lors de la génération du PDF.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section style={{ padding: 16 }}>
      <h2>Supprimer / Réordonner des pages (PDF)</h2>
      <small>Tout se fait dans ton navigateur (aucun envoi)</small>

      <Dropzone
        accept="application/pdf,.pdf"
        multiple={false}
        disabled={loading}
        acceptText="Dépose ton PDF ici"
        helperText="Glisse-dépose un PDF ou clique sur “Choisir un fichier”"
        onFiles={(files) => onPick(files?.[0] || null)}
      />

      {file && (
        <p style={{ textAlign: "center", color: "#666", marginTop: 8 }}>
          Fichier : <b>{file.name}</b>
          {typeof pdfInfo.count === "number" ? (
            <>
              {" "}
              — Pages : <b>{pdfInfo.count}</b> — Restantes : <b>{keptCount}</b>
            </>
          ) : null}
        </p>
      )}
      {
        <p
          style={{
            color: "#666",
            textAlign: "center",
            fontSize: 18,
            marginTop: 6,
          }}
        >
          Astuce : tu peux aussi supprimer une page en cliquant sur 🗑️ sur la
          miniature, et changer l’ordre en glissant.
        </p>
      }

      {/* THUMB GRID */}
      {pages.length > 0 && (
        <div
          style={{
            maxWidth: 980,
            margin: "14px auto 0",
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))",
            gap: 12,
          }}
        >
          {pages.map((p, idx) => (
            <div
              key={p.id}
              draggable={!loading}
              onDragStart={() => onDragStart(idx)}
              onDragOver={(e) => e.preventDefault()}
              onDrop={() => onDrop(idx)}
              style={{
                border: "1px solid #e6e6e6",
                borderRadius: 12,
                background: p.removed ? "#fff0f0" : "#fff",
                padding: 10,
                cursor: loading ? "not-allowed" : "grab",
                opacity: p.removed ? 0.65 : 1,
                position: "relative",
                userSelect: "none",
              }}
              title="Glisse pour réordonner"
            >
              {/* Delete icon */}
              <button
                type="button"
                onClick={() => removeOne(p.id)}
                disabled={loading}
                style={{
                  position: "absolute",
                  top: 8,
                  right: 8,
                  width: 32,
                  height: 32,
                  borderRadius: 10,
                  border: "1px solid #e6e6e6",
                  background: "#fff",
                  cursor: "pointer",
                }}
                aria-label={`Supprimer la page ${idx + 1} (origine ${
                  p.pageNum
                })`}
                title="Supprimer cette page"
              >
                🗑️
              </button>

              <img
                src={p.thumbUrl}
                alt={`Page ${idx + 1}`}
                style={{
                  width: "100%",
                  height: 160,
                  objectFit: "contain",
                  background: "#f4f4f4",
                  borderRadius: 10,
                }}
              />

              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginTop: 8,
                }}
              >
                <div style={{ fontWeight: 800, color: "#222" }}>
                  Page {idx + 1}
                </div>
                <button
                  type="button"
                  onClick={() => toggleRemoved(p.id)}
                  disabled={loading}
                  style={{
                    padding: "6px 10px",
                    borderRadius: 10,
                    border: "1px solid #e6e6e6",
                    background: p.removed ? "#ffffff" : "#fbfffb",
                    cursor: "pointer",
                    fontWeight: 700,
                    color: p.removed ? "#b00020" : "#2e7d32",
                  }}
                >
                  {p.removed ? "Annuler" : ""}
                </button>
              </div>

              <div style={{ color: "#666", fontSize: 12, marginTop: 4 }}>
                (Origine : page {p.pageNum})
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ACTIONS */}
      {pages.length > 0 && (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            gap: 12,
            flexWrap: "wrap",
            marginTop: 16,
          }}
        >
          <button
            type="button"
            onClick={downloadEditedPdf}
            disabled={loading || keptCount === 0}
            style={{ minWidth: 260 }}
          >
            {loading ? "Génération..." : "Télécharger le PDF final"}
          </button>

          <button
            type="button"
            onClick={resetAll}
            disabled={loading}
            style={{ minWidth: 160 }}
          >
            Réinitialiser
          </button>
        </div>
      )}
    </section>
  );
}
